import { Link } from "@mui/material";

const RemoteTeamActivities = () => {
  return (
    <div className="container--blog_post_page_wrapper">
      <section className="container--blog_post">
        <div className="container--post_content">
          <p className="text--post_paragraph">
            Research shows that 71% of employees currently work from home (
            <Link
              href="https://www.pewresearch.org/social-trends/2020/12/09/how-the-coronavirus-outbreak-has-and-hasnt-changed-the-way-americans-work/"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read about changes in American work habits due to the coronavirus outbreak on Pew Research Center"
            >
              Pewresearch.org
            </Link>
            ). Of these, many work in teams composed of people from across the
            globe. Despite the numbers, one cannot deny that the lack of
            personal interaction can and does take a toll on what you might call
            “team spirit.”
          </p>
          <p className="text--post_paragraph">
            The term remote work itself implies remoteness or physical
            distancing from other team members. The physical distance creates
            emotional distancing that leads to isolation, making employees
            uncomfortable about working alongside team members. In this age of
            increasing remote work, virtual team-building activities are the
            need of the hour, helping foster a sense of camaraderie and
            community. The collaborative environment that virtual team building
            facilitates leads to deeper bonds between team members.
          </p>
          <p className="text--post_paragraph">
            Let’s dive deep into the benefits of hosting virtual team meetings
            in the hopes of creating the right collaborative team culture and
            how you can make it work for you.
          </p>
          <h2 className="header header--page_title_h2_left">
            Strengthening Team Bonds Virtually
          </h2>
          <p className="text--post_paragraph">
            It’s no secret that project success relies on teamwork (
            <Link
              href="http://www.unice.fr/crookall-cours/teams/docs/team%20Successful%20teamwork.pdf"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read about the importance of teamwork in projects on Unice.fr"
            >
              Unice.fr
            </Link>
            ) between employees. However, with most team members operating
            hundreds of miles away from one another, it’s not hard to see why
            individual members don’t form bonds as easily as they would in a
            traditional office setting.
          </p>
          <p className="text--post_paragraph">
            Recent research by (
            <Link
              href="https://templatelab.com/wfh-study/"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read about productivity in remote workers from Stanford University's research"
            >
              Nbloom.people.stanford.edu
            </Link>
            ) shows that productivity in remote workers increased by up to 13%
            within nine months. The numbers are impressive, and remote working
            no doubt has positive economic, physical, and emotional benefits for
            employees. That said, it does impact team dynamics.
          </p>
          <p className="text--post_paragraph">
            Most remote work teams are formed over the internet and therefore
            have very little scope of interaction outside of work settings.
            Since there are no opportunities to grab a cup of coffee or a few
            drinks after work, rarely do you see friendships blossom.
          </p>
          <p className="text--post_paragraph">
            This is where virtual or digital team-building activities come in
            handy. According to a study by (
            <Link
              href="https://www.mindtools.com/pages/article/virtual-team-building-exercises.htm#:~:text=Virtual%20team%20building%20can%20also%20reduce%20feelings%20of%20isolation%20and,equally%20effective%20for%20virtual%20teams"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read about virtual team-building exercises on Mindtools.com"
            >
              Mindtools.com
            </Link>
            ), virtual team-building activities help improve trust and boost
            productivity amongst members. While the lack of physical connection
            may impact the speed at which your team interacts and gels together,
            it’s not at all impossible to ensure they bond.
          </p>
          <h2 className="header header--page_title_h2_left">
            Making Virtual Team Meetings Work
          </h2>
          <p className="text--post_paragraph">
            There are many ways to set up a virtual meet for your remote teams
            with team building in mind. While spending an entire day dedicated
            to working on something fun together may seem like the best option,
            you can also incorporate team bonding exercises into your everyday
            routine.
          </p>
          <p className="text--post_paragraph">
            For example, the 8% rule (Teambuilding.com) states that at least 8
            percent of any meeting should be dedicated to fun games and
            activities. That’s about 5 minutes for every hour a meeting runs.
            And when all team members are encouraged to participate, it can help
            cultivate a collaborative culture.
          </p>
          <p className="text--post_paragraph">
            This way, they’re able to engage and interact with fellow team
            members without the pressure of work.
          </p>
          <p className="text--post_paragraph">
            One of the easiest ways to encourage daily connections is by sharing
            memes on the team channel. You can also share daily jokes, fun
            facts, and set up challenges for every team member.
          </p>
          <p className="text--post_paragraph">
            You can also consider setting aside certain days for team-building
            exercises. And if you think it’s not easy to organize online team
            building activities, we’d like to reassure you that it’s not all
            that different from organizing a physical one.
          </p>
          <h2 className="header header--page_title_h2_left">
            Choosing The Right Virtual Team Building Activities
          </h2>
          <p className="text--post_paragraph">
            There are many virtual activities you can use to build the right
            team culture. However, not all of them may work for your team.
            Before setting up your virtual event, it’s important to keep the
            following points in mind:
          </p>
          <div className="text--post_bullet">
            <ul>
              ● &nbsp;Choose activities that are appropriate for each member
            </ul>
            <ul>
              ● &nbsp;Select challenges and exercises that are convenient and
              easy to understand
            </ul>
            <ul>● &nbsp;Focus on getting your team members to work together</ul>
            <ul>● &nbsp;Stick to a handful - there’s always next time</ul>
          </div>
          <p className="text--post_paragraph">
            It also helps to understand the factors that go into making any
            team-building activity empowering. These include:
          </p>
          <div className="text--post_bullet">
            <ul>● &nbsp;Open and honest communication</ul>
            <ul>● &nbsp;Unconditional support while taking risks</ul>
            <ul>● &nbsp;Sharing common goals</ul>
            <ul>● &nbsp;Supporting mutual accountability</ul>
            <ul>
              ● &nbsp;Providing an avenue for sharing opinions and suggestions
            </ul>
          </div>
          <p className="text--post_paragraph">
            With that in mind, let’s look at some virtual team building
            activities that effectively encourage more interaction in a virtual
            workspace, giving remote team members a feeling of belonging and
            keeping them happy. And happy teams lead to increased productivity.
          </p>
          <div style={{ paddingLeft: "2rem" }}>
            <h3 className="header header--page_title_h3_left">
              2 Truths And 1 Lie
            </h3>
            <p className="text--post_paragraph">
              This is a straightforward activity many are familiar with, making
              it the perfect icebreaker. It requires each member to say two real
              facts about them as well as one believable lie.
            </p>
            <p className="text--post_paragraph">
              Members will take turns sharing their statements while others vote
              on what they think is a fib. It’s a fun way to get to know fellow
              team members better.
            </p>
            <h3 className="header header--page_title_h3_left">
              The Sole Castaway
            </h3>
            <p className="text--post_paragraph">
              This is a popular activity to encourage critical thinking. Each
              participant is encouraged to visualize themselves stuck on a
              deserted island with only three things. The catch is that they may
              only use three of a given set of objects.
            </p>
            <p className="text--post_paragraph">
              The more random the objects, the more each team member is
              encouraged to think critically. To make things even more fun, you
              can split members into teams and set up private virtual rooms so
              they can collaborate amongst themselves.
            </p>
            <h3 className="header header--page_title_h3_left">
              Virtual Scavenger Hunts
            </h3>
            <p className="text--post_paragraph">
              This fun and quick game will keep everyone on their toes, waiting
              for your next instructions. And best of all? There’s no
              preparation needed - you can play it impromptu.
            </p>
            <p className="text--post_paragraph">
              To get the ball rolling, all the host needs to do is call out
              random objects or commands, and everyone has a few seconds to show
              up with the object or accomplish the task on camera.
            </p>
            <p className="text--post_paragraph">For example, you could say:</p>
            <div className="text--post_bullet">
              <ul>● &nbsp;A blue book</ul>
              <ul>● &nbsp;Lick a spoon</ul>
              <ul>● &nbsp;Do 10 jumping jacks</ul>
              <ul>● &nbsp;And so on</ul>
            </div>
            <h3 className="header header--page_title_h3_left">
              Ice Cream Vs. Cake
            </h3>
            <p className="text--post_paragraph">
              The perfect way to engage introverts on your team, this game
              starts with an age-old debate - ice cream or cake? The entire team
              will then need to discuss amongst themselves an answer that the
              majority will agree to.
            </p>
            <p className="text--post_paragraph">
              Once the team has decided, any member can nominate another person
              to propose another debate. For example, the next person could say
              “gin or vodka.”
            </p>
          </div>
          <h2 className="header header--page_title_h2_left">Final Thoughts</h2>
          <p className="text--post_paragraph">
            Over the course of the pandemic and the global switch to remote
            work, many companies have begun to put together entire teams
            virtually. While it may be challenging, there’s no denying the fact
            that virtual team meetings play an important role in how effective
            and tight-knit the team culture becomes.
          </p>
          <p className="text--post_paragraph">
            Over the course of the pandemic and the global switch to remote
            work, many companies have begun to put together entire teams
            virtually. While it may be challenging, there’s no denying the fact
            that virtual team meetings play an important role in how effective
            and tight-knit the team culture becomes.
          </p>
        </div>
        <hr className="hr--form_break" />
      </section>
    </div>
  );
};

export default RemoteTeamActivities;
